// ─────────────────────────────────────────────────────────────────────────────
// pricingEngine.js  —  SINGLE SOURCE OF TRUTH (frontend)
//
// PARAMETERS:
//  basePriceExclGST   — Amazon price WITHOUT GST → our quoted/display price base
//  amazonPriceWithGST — Amazon price WITH GST    → threshold classification only
//  weight             — product weight in kg
//  quantity           — number of units
//  deliveryType       — "normal" | "express"
//
// CLASSIFICATION:
//  Light  = weight ≤ 2kg
//  Heavy  = weight > 2kg
//  Low    = amazonPriceWithGST < ₹50,000
//  High   = amazonPriceWithGST ≥ ₹50,000
//
// DISCOUNT MATRIX (% off basePriceExclGST):
//  Qty 1–2     Light Low → 2.1%   Light High → 1.5%   Heavy → 1.0%
//  Qty 3–5     Light Low → 4.1%   Light High → 3.5%   Heavy → 2.5%
//  Qty 6–9     Light Low → 5.4%   Light High → 5.2%   Heavy → 3.8%
//  Qty 10–49   Light Low → 9.5%   Light High → 9.0%   Heavy → 5.5%
//  Qty 50–100  Light Low → 10.0%  Light High → 9.0%   Heavy → 6.0%
//  Qty 101–500 Light Low → 11.0%  Light High → 9.0%   Heavy → 7.0%
//  Qty 500+    Light     → 9.0%                        Heavy → 6.0%
//
// EXPRESS DELIVERY: +₹450 per kg per unit added to display price
// STANDARD DELIVERY: no shipping surcharge
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {number} basePriceExclGST    - Amazon price excl. GST (our price base)
 * @param {number} amazonPriceWithGST  - Amazon price incl. GST (for classification)
 * @param {number} weight              - product weight in kg
 * @param {number} quantity            - units ordered
 * @param {"normal"|"express"} deliveryType
 * @returns {{
 *   finalPrice: number,
 *   perUnit: number,
 *   discountPct: number,
 *   expressShippingCost: number,
 *   savings: number,
 *   savingsPct: number,
 *   isLight: boolean,
 *   isLowPrice: boolean,
 * }}
 */
export function calculatePrice(
  basePriceExclGST,
  amazonPriceWithGST,
  weight = 1.0,
  quantity = 1,
  deliveryType = "normal",
) {
  const isLight    = weight <= 2;
  const isLowPrice = amazonPriceWithGST < 50000;

  // ── Discount lookup ──────────────────────────────────────────────────────
  let discountPct = 0;

  if (quantity >= 500) {
    discountPct = isLight ? 9.0 : 6.0;
  } else if (quantity >= 101) {
    discountPct = isLight ? (isLowPrice ? 11.0 : 9.0) : 7.0;
  } else if (quantity >= 50) {
    discountPct = isLight ? (isLowPrice ? 10.0 : 9.0) : 6.0;
  } else if (quantity >= 10) {
    discountPct = isLight ? (isLowPrice ? 9.5  : 9.0) : 5.5;
  } else if (quantity >= 6) {
    discountPct = isLight ? (isLowPrice ? 5.4  : 5.2) : 3.8;
  } else if (quantity >= 3) {
    discountPct = isLight ? (isLowPrice ? 4.1  : 3.5) : 2.5;
  } else {
    // qty 1–2
    discountPct = isLight ? (isLowPrice ? 2.1  : 1.5) : 1.0;
  }

  // ── Price calculation ────────────────────────────────────────────────────
  const discountedUnitPrice    = basePriceExclGST * (1 - discountPct / 100);
  const expressShippingPerUnit = deliveryType === "express" ? weight * 450 : 0;
  const perUnit                = Math.round(discountedUnitPrice + expressShippingPerUnit);
  const finalPrice             = perUnit * quantity;
  const expressShippingCost    = Math.round(expressShippingPerUnit * quantity);

  // Savings vs Amazon excl-GST undiscounted price
  const originalTotal = Math.round(basePriceExclGST * quantity);
  const savings       = Math.max(0, originalTotal - finalPrice);
  const savingsPct    = originalTotal > 0
    ? Math.round((savings / originalTotal) * 100)
    : 0;

  return {
    finalPrice,
    perUnit,
    discountPct,
    expressShippingCost,
    savings,
    savingsPct,
    isLight,
    isLowPrice,
  };
}

// ─── Safe Indian-rupee formatter ─────────────────────────────────────────────
export function formatINR(n) {
  const num = Number(n);
  return isFinite(num) ? num.toLocaleString("en-IN") : "0";
}